import { type JSX, useEffect, useState } from 'react';
import { type EventData, Joyride, STATUS, type Step } from 'react-joyride';

import { hasSeen, markSeen } from './persistence.js';

export interface TourProps {
  /** Identifier used for localStorage seen-flag persistence. */
  id: string;
  /** Tour step definitions. Targets are CSS selectors. */
  steps: readonly Step[];
  /**
   * When true, run the tour after a short delay so the targets have
   * time to mount. Callers gate this on "user has not seen this tour
   * AND the relevant UI is mounted." Defaults true.
   */
  run?: boolean;
  /** Override delay before the tour starts (ms). Default 600ms. */
  startDelayMs?: number;
}

/**
 * Thin react-joyride v3 wrapper that:
 *   - Waits for `document.readyState === 'complete'` (load event fired,
 *     stylesheets + fonts settled) before considering the tour eligible.
 *     A tour that starts mid-paint can grab the wrong layout coordinates
 *     for its targets and end up pointing at empty space.
 *   - Respects the caller's `run` gate (set false until iframes /
 *     preview panes are also ready — see BuilderEditor's previewReady).
 *   - Then defers start by `startDelayMs` so freshly-mounted targets
 *     have time to settle.
 *   - Persists "seen" flag to localStorage on finish or skip so users
 *     don't get pestered after their first run-through.
 *   - Applies a dark-mode color palette via the Options object (v3's
 *     flat options shape replaces v2's styles.options nesting).
 *   - Skips the v2-style beacon (`options.skipBeacon`) so the first
 *     step shows immediately — a beacon click step is confusing on a
 *     one-shot welcome tour.
 */
export function Tour({ id, steps, run = true, startDelayMs = 600 }: TourProps): JSX.Element | null {
  const [active, setActive] = useState(false);
  const [docReady, setDocReady] = useState<boolean>(() =>
    typeof document !== 'undefined' && document.readyState === 'complete',
  );

  // Track document.readyState — listen for the `load` event (fires
  // when the page + all sub-resources finish loading) so we don't show
  // a tour against half-painted UI.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (document.readyState === 'complete') {
      setDocReady(true);
      return undefined;
    }
    function onLoad(): void {
      setDocReady(true);
    }
    window.addEventListener('load', onLoad);
    return () => {
      window.removeEventListener('load', onLoad);
    };
  }, []);

  useEffect(() => {
    if (!run || !docReady) return undefined;
    if (hasSeen(id)) return undefined;
    const handle = window.setTimeout(() => setActive(true), startDelayMs);
    return () => {
      window.clearTimeout(handle);
    };
  }, [id, run, docReady, startDelayMs]);

  function onEvent(data: EventData): void {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      markSeen(id);
      setActive(false);
    }
  }

  if (!active) return null;

  return (
    <Joyride
      steps={[...steps]}
      run
      continuous
      onEvent={onEvent}
      options={{
        backgroundColor: '#0b0c0f',
        textColor: '#e8eaed',
        arrowColor: '#0b0c0f',
        primaryColor: '#1976d2',
        overlayColor: 'rgba(0, 0, 0, 0.55)',
        zIndex: 10000,
        skipBeacon: true,
        showProgress: true,
        buttons: ['back', 'skip', 'primary'],
      }}
      styles={{
        tooltip: { borderRadius: 8, fontSize: 14 },
        tooltipContent: { padding: '0.5rem 0' },
        tooltipTitle: { color: '#ffffff', fontSize: 16, fontWeight: 600 },
        buttonPrimary: {
          borderRadius: 6,
          padding: '0.45rem 0.9rem',
        },
        buttonBack: { color: 'rgba(232, 234, 237, 0.7)' },
        buttonSkip: { color: 'rgba(232, 234, 237, 0.55)' },
        buttonClose: { color: 'rgba(232, 234, 237, 0.6)' },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Got it',
        next: 'Next',
        skip: 'Skip',
      }}
    />
  );
}
